import { convert, LocalDate, nativeJs } from "@js-joda/core";
import {
  CalendarTileProperties as ReactCalendarTileProperties,
  ClickWeekNumberCallback as ReactClickWeekNumberCallback,
  DateCallback as ReactDateCallback,
  Detail,
  FormatterCallback as ReactFormatterCallback,
  OnChangeDateCallback as ReactOnChangeDateCallback,
  ViewCallback as ReactViewCallback,
  ViewCallbackProperties as ReactViewCallbackProperties,
} from "react-calendar";
import { fmap } from "../../../../utils/fmap";
import { nullToUndefined } from "../../../TrainingCenter/TrainingCenterLanguageLookup";
import {
  MfCalendarTileProperties,
  MfClickWeekNumberCallback,
  MfDateCallback,
  MfFormatterCallback,
  MfOnChangeDateCallback,
  MfViewCallback,
} from "../CalendarPickerTypes";

export const convertLocalDateToDate = (date: LocalDate | undefined) =>
  nullToUndefined(fmap(date, (x) => convert(x).toDate()));

export const convertLocalDateToDate2 = (date: LocalDate[] | LocalDate | null | undefined) =>
  nullToUndefined(
    fmap(date, (x) => (Array.isArray(x) ? x.map((y) => convert(y).toDate()) : convert(x).toDate()))
  );

export const convertDateCallback = (fn?: MfDateCallback): ReactDateCallback | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (date: Date) => fn(LocalDate.from(nativeJs(date)));
};

export const convertClickWeekNumberCallback = (
  fn?: MfClickWeekNumberCallback
): ReactClickWeekNumberCallback | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (weekNumber: number, date: Date) => fn(weekNumber, LocalDate.from(nativeJs(date)));
};

export const convertOnChangeDateCallback = (
  fn?: MfOnChangeDateCallback
): ReactOnChangeDateCallback | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (date: Date | Date[]) => fn(LocalDate.from(nativeJs(date)));
};

export const convertFormatterCallback = (
  fn?: MfFormatterCallback
): ReactFormatterCallback | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (locale: string, date: Date) => fn(locale, LocalDate.from(nativeJs(date)));
};

export const convertViewCallback = (fn?: MfViewCallback): ReactViewCallback | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (fnProps: ReactViewCallbackProperties) =>
    fn({
      ...fnProps,
      activeStartDate: LocalDate.from(nativeJs(fnProps.activeStartDate)),
    });
};

export const convertNavigationLabel = (
  fn?: (props: { date: LocalDate; view: Detail; label: string }) => string | JSX.Element | null
):
  | ((props: { date: Date; view: Detail; label: string }) => string | JSX.Element | null)
  | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (fnProps) => fn({ ...fnProps, date: LocalDate.from(nativeJs(fnProps.date)) });
};

export const convertTileClassName = (
  value?: string | string[] | ((props: MfCalendarTileProperties) => string | string[] | null)
):
  | (string | string[] | ((props: ReactCalendarTileProperties) => string | string[] | null))
  | undefined => {
  if (value === undefined || typeof value === "string" || Array.isArray(value)) {
    return value;
  }

  return (fnProps) =>
    value({
      ...fnProps,
      date: LocalDate.from(nativeJs(fnProps.date)),
      activeStartDate: LocalDate.from(nativeJs(fnProps.activeStartDate)),
    });
};

export const convertTileContent = (
  value?: JSX.Element | ((props: MfCalendarTileProperties) => JSX.Element | null)
): (JSX.Element | ((props: ReactCalendarTileProperties) => JSX.Element | null)) | undefined => {
  if (value === undefined || typeof value !== "function") {
    return value;
  }

  return (fnProps) =>
    value({
      ...fnProps,
      date: LocalDate.from(nativeJs(fnProps.date)),
      activeStartDate: LocalDate.from(nativeJs(fnProps.activeStartDate)),
    });
};

export const convertTileDisabled = (
  fn?: (props: MfCalendarTileProperties) => boolean
): ((props: ReactCalendarTileProperties) => boolean) | undefined => {
  if (fn === undefined) {
    return undefined;
  }

  return (fnProps) =>
    fn({
      ...fnProps,
      date: LocalDate.from(nativeJs(fnProps.date)),
      activeStartDate: LocalDate.from(nativeJs(fnProps.activeStartDate)),
    });
};
